import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import KpiCard from './KpiCard';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const SectionHead = styled(Typography)({
  fontSize: '11px',
  fontWeight: 500,
  color: '#9b9b97',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  fontFamily: '"SF Mono", "Fira Code", monospace',
  marginBottom: '10px',
});

const Grid4 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '10px',
  marginBottom: '14px',
});

const Grid2 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '10px',
  marginBottom: '14px',
});

const ChartCard = styled(Box)({
  backgroundColor: '#ffffff',
  border: '0.5px solid #e0ddd6',
  borderRadius: '12px',
  padding: '16px',
  overflow: 'hidden',
});

const CHART_GRID = '#e0ddd633';
const CHART_TICK = '#9b9b97';

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      labels: { color: CHART_TICK, font: { size: 11 }, boxWidth: 10, boxHeight: 10 },
    },
    tooltip: {
      backgroundColor: 'rgba(0,0,0,0.75)',
      padding: 8,
      titleFont: { size: 11 },
      bodyFont: { size: 11 },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: CHART_TICK, font: { size: 10 }, maxRotation: 0 },
    },
    y: {
      grid: { color: CHART_GRID },
      ticks: { color: CHART_TICK, font: { size: 10 } },
      border: { dash: [3, 3] },
    },
  },
};

export default function BusinessTab({ data }) {
  if (!data) return null;
  const d = data;

  const revenueData = {
    labels: ['Gross Revenue', 'Net Revenue'],
    datasets: [
      {
        label: 'Gross',
        data: [parseRevenue(d.grossRevenue), 0],
        backgroundColor: 'rgba(127,119,221,0.15)',
        borderColor: '#7F77DD',
        borderWidth: 1.5,
        borderRadius: 3,
      },
      {
        label: 'Net',
        data: [0, parseRevenue(d.netRevenue)],
        backgroundColor: 'rgba(29,158,117,0.15)',
        borderColor: '#1D9E75',
        borderWidth: 1.5,
        borderRadius: 3,
      },
    ],
  };

  return (
    <Box>
      <SectionHead>Overview</SectionHead>
      <Grid4>
        <KpiCard label="Total requests" value={d.totalRequests} subtitle="all service types" />
        <KpiCard label="Completed jobs" value={d.completedJobs} subtitle="↑ completion" subtitleColor="pos" />
        <KpiCard label="Completion rate" value={d.completionRate} subtitle="target > 85%" subtitleColor="pos" />
        <KpiCard label="Cancellation rate" value={d.cancellationRate} subtitle={getCancelSub(d.cancellationRate)} subtitleColor={getCancelColor(d.cancellationRate)} />
      </Grid4>

      <Grid4>
        <KpiCard label="Gross revenue" value={d.grossRevenue} subtitle="before deductions" />
        <KpiCard label="Net revenue" value={d.netRevenue} subtitle="after ops costs" />
        <KpiCard label="Active users" value={d.activeUsers} subtitle="unique this period" />
        <KpiCard label="Active valets" value={d.activeValets} subtitle="on platform" />
      </Grid4>

      <SectionHead sx={{ marginTop: '4px' }}>Performance</SectionHead>
      <Grid2>
        <KpiCard label="Avg pickup ETA" value={d.avgPickupETA} subtitle="dispatch to arrival" />
        <KpiCard label="Avg retrieval time" value={d.avgRetrieveTime} subtitle="request to delivery" />
      </Grid2>

      <SectionHead sx={{ marginTop: '4px' }}>Revenue</SectionHead>
      <ChartCard>
        <Box sx={{ height: 180 }}>
          <Bar data={revenueData} options={chartOptions} />
        </Box>
      </ChartCard>
    </Box>
  );
}

function parseRevenue(str) {
  if (typeof str === 'number') return str;
  if (!str) return 0;
  return Number(String(str).replace(/[^0-9.-]/g, '')) || 0;
}

function getCancelColor(rate) {
  const val = parseFloat(rate);
  return val > 10 ? 'neg' : 'neu';
}

function getCancelSub(rate) {
  return 'target < 10%';
}
